import type { Category, FileType } from './types/mime'
import { mdiCodeJson, mdiFile, mdiExclamationThick, mdiFileCode, mdiFilePdfBox, mdiFolder, mdiImage, mdiLanguageJavascript, mdiLanguageMarkdown, mdiNoteText, mdiFilePresentationBox, mdiFileDocument, mdiMicrosoftExcel, mdiMicrosoftPowerpoint, mdiMicrosoftWord } from '@mdi/js'
import { Mime } from './enum/Mime'
import { MimeCategory } from './types/mime'

export const fileTypes: Partial<Record<Mime, FileType>> = Object.freeze({
  [Mime['text/csv']]: { actions: { edit: true, delete: true, download: true, move: true, copy: true }, category: MimeCategory.Text, icon: mdiNoteText },
  [Mime['text/plain']]: { actions: { edit: true, delete: true, download: true, move: true, copy: true }, category: MimeCategory.Text, icon: mdiNoteText },
  [Mime['text/markdown']]: { actions: { edit: true, delete: true, move: true, copy: true, download: true, convertManual: true, convertTemplate: true }, category: MimeCategory.Markdown, icon: mdiLanguageMarkdown },
  [Mime['application/text']]: { actions: { edit: true, delete: true, download: true, move: true, copy: true }, category: MimeCategory.Text, icon: mdiNoteText },
  [Mime['application/json']]: { actions: { delete: true, download: true, move: true, copy: true }, category: MimeCategory.Code, icon: mdiCodeJson },
  [Mime['application/javascript']]: { actions: { delete: true, download: true, move: true, copy: true }, category: MimeCategory.Code, icon: mdiLanguageJavascript },
  [Mime['application/pdf']]: { actions: { delete: true, download: true, move: true, copy: true }, category: MimeCategory.Pdf, icon: mdiFilePdfBox },
  [Mime['image/jpeg']]: { actions: { download: true, delete: true, edit: true, move: true, copy: true }, category: MimeCategory.Image, icon: mdiImage },
  [Mime['image/png']]: { actions: { download: true, delete: true, edit: true, move: true, copy: true }, category: MimeCategory.Image, icon: mdiImage },
  [Mime['image/webp']]: { actions: { download: true, delete: true, edit: true, move: true, copy: true }, category: MimeCategory.Image, icon: mdiImage },
  [Mime['image/gif']]: { actions: { download: true, delete: true, edit: false, move: true, copy: true }, category: MimeCategory.Image, icon: mdiImage },
  [Mime['application/yaml']]: { actions: { download: true }, category: MimeCategory.Code, icon: mdiExclamationThick },
  [Mime['application/vnd.openxmlformats-officedocument.presentationml.presentation']]: { actions: { delete: true, download: true, copy: true, move: true }, category: MimeCategory.Office, icon: mdiFilePresentationBox },
  [Mime['application/vnd.ms-powerpoint']]: { actions: { delete: true, download: true, move: true, copy: true }, category: MimeCategory.Office, icon: mdiMicrosoftPowerpoint },
  [Mime['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']]: { actions: { delete: true, download: true, move: true, copy: true }, category: MimeCategory.Office, icon: mdiMicrosoftExcel },
  [Mime['application/vnd.ms-excel']]: { actions: { delete: true, download: true, move: true, copy: true }, category: MimeCategory.Office, icon: mdiMicrosoftExcel },
  [Mime['application/vnd.openxmlformats-officedocument.wordprocessingml.document']]: { actions: { delete: true, download: true, move: true, copy: true }, category: MimeCategory.Office, icon: mdiMicrosoftWord },
  [Mime['application/msword']]: { actions: { delete: true, download: true, move: true, copy: true }, category: MimeCategory.Office, icon: mdiMicrosoftWord },
  [Mime.raw]: { actions: { delete: true, move: true, copy: true }, category: MimeCategory.Raw, icon: mdiFile },
})

export const categories: Partial<Record<MimeCategory, Category>> = Object.freeze({
  [MimeCategory.Code]: { viewMode: false, icon: mdiFileCode },
  [MimeCategory.Image]: { viewMode: true, icon: mdiImage },
  [MimeCategory.Markdown]: { viewMode: true, icon: mdiLanguageMarkdown, indexing: true },
  [MimeCategory.Pdf]: { viewMode: true, icon: mdiFilePdfBox, indexing: true },
  [MimeCategory.Text]: { viewMode: false, icon: mdiNoteText, indexing: true },
  [MimeCategory.Folder]: { viewMode: true, icon: mdiFolder },
  [MimeCategory.Office]: { viewMode: true, icon: mdiFileDocument, indexing: true },
  [MimeCategory.Raw]: { viewMode: false, icon: mdiFile },
})

const extensionToMime: Record<string, Mime> = {
  md: Mime['text/markdown'],
  txt: Mime['text/plain'],
  csv: Mime['text/csv'],
  json: Mime['application/json'],
  js: Mime['application/javascript'],
  pdf: Mime['application/pdf'],
  png: Mime['image/png'],
  jpg: Mime['image/jpeg'],
  jpeg: Mime['image/jpeg'],
  webp: Mime['image/webp'],
  svg: Mime['image/svg+xml'],
  gif: Mime['image/gif'],
  yaml: Mime['application/yaml'],
  yml: Mime['application/yaml'],
  pptx: Mime['application/vnd.openxmlformats-officedocument.presentationml.presentation'],
  xls: Mime['application/vnd.ms-excel'],
  ppt: Mime['application/vnd.ms-powerpoint'],
  doc: Mime['application/msword'],
  docx: Mime['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  xlsx: Mime['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
}

/**
 * Returns the MIME type for a given filename based on its extension.
 * Correctly handles dotfiles like ".md" where the name starts with a dot.
 */
export function getMimeFromFilename(filename: string): Mime | undefined {
  const parts = filename.split('.')
  if (parts.length < 2)
    return undefined
  const ext = parts[parts.length - 1].toLowerCase()
  return extensionToMime[ext]
}

/**
 * Normalizes a MIME type string from an external source (e.g. WebDAV server).
 * If the provided mime is not a recognized Mime value, falls back to
 * extension-based detection using the filename.
 */
export function normalizeMime(filename: string, mimeFromSource: string | undefined): Mime | undefined {
  if (mimeFromSource) {
    const knownMimes = Object.values(Mime) as string[]
    if (knownMimes.includes(mimeFromSource))
      return mimeFromSource as Mime
  }
  return getMimeFromFilename(filename)
}

export function isCategory(type: Mime, category: MimeCategory): boolean {
  return fileTypes[type]?.category === category
}

export function getMimeCategory(type: Mime): MimeCategory {
  return fileTypes[type]?.category ?? MimeCategory.Raw
}

export function viewModeMimes(): Mime[] {
  return Object.keys(fileTypes).filter((type) => {
    const m = fileTypes[type as Mime]
    if (!m)
      return false
    return categories[m.category]?.viewMode === true
  }).map(key => key as Mime)
}

export function isViewModeAllowed(type: Mime | null, isFolder?: boolean): boolean {
  if (type === null)
    return false
  if (isFolder)
    return true
  const category = getMimeCategory(type)
  return categories[category]?.viewMode ?? false
}

export function isIndexingAllowed(type: Mime | null): boolean {
  if (type === null)
    return false
  const category = getMimeCategory(type)
  return categories[category]?.indexing ?? false
}
